import type { RBACGroup, RBACUser } from '../../../types';
import { useQueryErrorToast } from '../../graph/hooks/useQueryErrorToast';
import {
  useGetUserGroupMembershipQuery,
  useGetUserMembershipInheritanceQuery,
  useGetUserTransitiveGroupMembershipQuery,
} from '../../graph/services/graphApi';

import ACLGroupList from './ACLGroupList';
import RoleGroupList from './RoleGroupList';

type Props = {
  user?: RBACUser;
  // When false, show only direct memberships (and their ACLs); the toggle that
  // controls this lives up in the search area (routes/users.tsx).
  showTransitive?: boolean;
  // All role groups available to add, surfaced in the Role membership panel's search box.
  roleOptions?: RBACGroup[];
};

export default function UserGroupMembership({ user, showTransitive = false, roleOptions }: Props) {
  const { data: directMembership, isError: directError } = useGetUserGroupMembershipQuery(
    user?.id || '',
    {
      skip: !user, // Skip query if no user selected
    }
  );
  const { data: transitiveMembership, isError: transitiveError } =
    useGetUserTransitiveGroupMembershipQuery(user?.id || '', {
      skip: !user || !showTransitive,
    });
  const { data: inheritance, isError: inheritanceError } = useGetUserMembershipInheritanceQuery(
    user?.id || '',
    {
      skip: !user || !showTransitive,
    }
  );
  useQueryErrorToast(directError, 'Failed to load role memberships');
  useQueryErrorToast(transitiveError, 'Failed to load transitive memberships');
  useQueryErrorToast(inheritanceError, 'Failed to load membership provenance');

  if (!user) {
    return <div></div>;
  }

  // /transitiveMemberOf is a superset of /memberOf: the roles in it but NOT in
  // the direct list are inherited via nested groups. Surface those (already
  // flagged transitive: true by the query) alongside the direct ones, so
  // RoleGroupList can badge them — GroupDisplayItem also makes them
  // non-deletable, since you can't unassign an inherited membership here.
  const directIds = new Set((directMembership ?? []).map((group) => group.id));
  const inheritedMembership = showTransitive
    ? (transitiveMembership ?? []).filter((group) => !directIds.has(group.id))
    : [];
  const roleMembership = directMembership
    ? [...directMembership, ...inheritedMembership]
    : undefined;

  return (
    <div className="flex flex-row gap-4 h-full min-h-0">
      <div className="w-6/12 min-h-0 flex flex-col">
        <RoleGroupList
          groups={roleMembership}
          inheritance={inheritance}
          roleOptions={roleOptions}
          enabled={!!user}
        />
      </div>
      {/* Calculated ACLs reflect EFFECTIVE access — direct + inherited roles —
          so selecting a transitive role highlights the ACLs it grants. The
          directly-held transitive ACLs are gated on the toggle via userId. */}
      <div className="w-6/12 min-h-0 flex flex-col">
        <ACLGroupList
          groups={roleMembership}
          inheritance={inheritance}
          userId={showTransitive ? user.id : undefined}
        />
      </div>
    </div>
  );
}
